import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { TechLogo } from '../../../data/techStack';
import styles from './LogoLoop.module.css';

interface LogoLoopProps {
    logos: TechLogo[];
    label: string;
}

const INITIAL_COPIES = 2;

export default function LogoLoop({ logos, label }: LogoLoopProps) {
    const loopRef = useRef<HTMLDivElement>(null);
    const firstSetRef = useRef<HTMLDivElement>(null);
    const [copies, setCopies] = useState(INITIAL_COPIES);

    useEffect(() => {
        const loop = loopRef.current;
        const firstSet = firstSetRef.current;
        if (!loop || !firstSet) return;

        const coverLoop = () => setCopies(Math.ceil(loop.offsetWidth / firstSet.offsetWidth) + 1);

        const observer = new ResizeObserver(coverLoop);
        observer.observe(loop);
        observer.observe(firstSet);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={loopRef} className={styles.loop} role="region" aria-label={label}>
            <ul className="visually-hidden">
                {logos.map((logo) => <li key={logo.name}>{logo.name}</li>)}
            </ul>
            <div className={styles.track} style={{ '--copies': copies } as CSSProperties} aria-hidden="true">
                {Array.from({ length: copies }, (_, copy) => (
                    <div key={copy} ref={copy === 0 ? firstSetRef : undefined} className={styles.set}>
                        {logos.map((logo) => (
                            <span key={logo.name} className={styles.logo} title={logo.name}>
                                <svg viewBox="0 0 24 24" className={styles.mark} focusable="false">
                                    <path d={logo.path} fill="currentColor" />
                                </svg>
                                <span className={styles.name}>{logo.name}</span>
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}
